import { isDefined } from '@sgme/fp';
import { FormattedMessage } from 'react-intl';

import { selectCurrencyPairPrecision } from '@/api/config/selectors.ts';
import { FxFormattedNumber } from '@/App/tiles/Option/common/FxFormattedNumber.tsx';
import { OptionFormGroup } from '@/App/tiles/Option/fields/OptionFormGroup.tsx';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useAppSelector } from '@/store/hooks.ts';
import { getQuote, selectOptionCurrency } from '@/store/option/selectors/optionSelectors.ts';
import { selectEspSpot } from '@/store/option/selectors/selectors.ts';
import type { BidAsk } from '@/types/common.ts';

type SpotFieldProps = {
  tileId: string;
};

type SpotDisplayProps = { type: 'rfs'; value: BidAsk<number> } | { type: 'esp'; value: number } | { type: 'none' };

export function OptionSpot({ tileId }: SpotFieldProps) {
  const spot: SpotDisplayProps = useOptionSelector((option, state) => {
    const rfsSpot = getQuote(option)?.spot;
    const espSpot = selectEspSpot(state, tileId);
    const isExpired = option.properties.rfs.status === 'EXPIRED';

    if (isDefined(rfsSpot) && !isExpired) {
      return { type: 'rfs', value: rfsSpot };
    }
    if (isDefined(espSpot)) {
      return { type: 'esp', value: espSpot };
    }
    return { type: 'none' };
  });

  return (
    <OptionFormGroup label={<FormattedMessage id="option.fields.spot" />}>
      <SpotDisplay spot={spot} />
    </OptionFormGroup>
  );
}

function SpotDisplay({ spot }: { spot: SpotDisplayProps }) {
  const { currencyPair } = useOptionSelector(selectOptionCurrency);
  const currencyPairPrecision = useAppSelector((state) => selectCurrencyPairPrecision(state, currencyPair));

  switch (spot.type) {
    case 'esp':
      return <FxFormattedNumber value={spot.value} maximumFractionDigits={currencyPairPrecision} />;
    case 'rfs':
      return (
        <div data-e2e="rfs-spot">
          <FxFormattedNumber value={spot.value.bid} maximumFractionDigits={currencyPairPrecision} />
          <span> / </span>
          <FxFormattedNumber value={spot.value.ask} maximumFractionDigits={currencyPairPrecision} />
        </div>
      );

    case 'none':
      return <span className="text-secondary">-</span>;
  }
}
